// Desc: Edit user permissions page
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Card, Space, Switch, InputNumber, Select, Tag, Spin, Row, Col, DatePicker, Popconfirm, Checkbox, Radio } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { getUserPermissions, updateUserPermissions, getBundlesByPermissionId, deletePermissionsBundle } from '../../services/api';
import BundleModal from './BundleModal';
import { timeConverter } from '../utils/TimeConverter';
import './Permissions.scss';

import deviceTypeOptions, { pcSubOptions }  from '../utils/deviceTypeOptions';
import  clientModeOptions  from '../utils/clientModeOptions';

const { Option } = Select;


const formatTime = (dateString) => {
  //去掉分秒
  return timeConverter.utcToLocal(dateString).split(' ')[0];
};

const calculateDays = (startTime, endTime) => {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  
  endDate.setDate(endDate.getDate() + 1);

  const calculateDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
  return calculateDays;
};

const BundleList = React.memo(({ bundles, permissionId, onEditBundle, onAssignBundle, onDeleteBundle }) => (
  <div style={{ marginTop: 16 }}>
    <h4>分配給此授權的 Bundles:</h4>
    <Row gutter={[16, 16]}>
      {(bundles || []).map(item => (
        <Col xs={24} sm={12} md={8} key={item.id}>
          <Card
            title={<Tag color={calculateDays(Date.now(), item.expire_time) >= 0 ? "green" : "red"}>ID:{item.id} | {item.name}</Tag>}
            style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', height: '100%' }}
            bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            <Space direction="vertical" style={{ marginBottom: 'auto' }}>
              <Tag color="blue">開始時間 : {formatTime(item.start_time)}</Tag>
              <Tag color="blue">到期時間 : {formatTime(item.expire_time)}</Tag>
              <Tag color="blue">總共 : {calculateDays(item.start_time, item.expire_time)} 天</Tag>
              <Tag color={calculateDays(Date.now(), item.expire_time) >= 0 ? "green" : "red"}>距到期日剩餘 : {calculateDays(Date.now(), item.expire_time)} 天</Tag>
            </Space>
            <Space style={{ marginTop: 'auto', width: '100%' }}>
              <Button
                icon={<EditOutlined />}
                onClick={() => onEditBundle(permissionId, item)}
                style={{ flex: 1 }}
              >
                Edit
              </Button>
              <Popconfirm
                title="Are you sure you want to delete this bundle?"
                onConfirm={() => onDeleteBundle(permissionId, item.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  style={{ flex: 1 }}
                >
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
    <Button
      type="dashed"
      onClick={() => onAssignBundle(permissionId)}
      icon={<PlusOutlined />}
      style={{ marginTop: 8 }}
    >
      Assign Bundle
    </Button>
  </div>
));

const EditUserPermissions = ({ setTitle }) => {
  const [form] = Form.useForm();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [bundlesData, setBundlesData] = useState({});
  const [isBundleModalVisible, setIsBundleModalVisible] = useState(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundleModalMode, setBundleModalMode] = useState('assign');

  const bundles = useMemo(() => bundlesData, [bundlesData]);

  const fetchUserPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const userData = await getUserPermissions(userId);
      setUser(userData);
      
      // 處理各個permission的初始值
      const formattedPermissions = userData.permissions.map(p => {
        // 判斷是否為PC類型(僅包含0,1,2這些值)
        const typeValues = p.type ? p.type.split(',') : [];
        const isPcType = typeValues.length > 0 && 
                         typeValues.every(v => ['0', '1', '2'].includes(v));
        
        // 基本數據
        const permissionData = {
          id: p.id,
          client_mode: p.client_mode,
          active: p.active === 1,
          current_bindings: p.current_bindings,
          max_bindings: p.max_bindings,
          ip_check: p.ip_check,
          ip: p.ip,
          caster_on: p.caster_on === 1,
          created_at: p.created_at
        };
        
        // 如果是PC類型，設置type為PC，並且將真實值存儲為單一值而非數組
        if (isPcType) {
          permissionData.type = 'PC';
          // 從原始值中取單一值 (如果有多個值，取第一個作為初始選中值)
          permissionData.pcSubTypes = typeValues.length > 0 ? typeValues[0] : "";
          permissionData._actualType = typeValues.length > 0 ? typeValues[0] : ""; // 存儲原始值用於提交
        } else {
          permissionData.type = p.type;
          
          // 如果是MASTER類型(type=5)且有client_types，設置初始值
          if (p.type === '5' && p.client_types) {
            permissionData.masterClientTypes = p.client_types.split(',');
            permissionData.client_types = p.client_types;
          }
        }
        
        return permissionData;
      });
      
      form.setFieldsValue({
        name: userData.name,
        permissions: formattedPermissions
      });

      const bundlePromises = userData.permissions.map(p => getBundlesByPermissionId(userId, p.id));
      const bundleResults = await Promise.all(bundlePromises);
      const bundleMap = {};
      userData.permissions.forEach((p, index) => {
        bundleMap[p.id] = bundleResults[index];
      });
      setBundlesData(bundleMap);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      message.error('Failed to fetch user permissions');
    }
    setLoading(false);
  }, [userId, form]);

  useEffect(() => {
    setTitle('EarthVerse CRM - Edit User Permissions');
    fetchUserPermissions();
  }, [setTitle, fetchUserPermissions]);

  const onFinish = useCallback(async (values) => {
    try {
      const updatedPermissions = values.permissions.map(p => {
        // 創建一個基本對象，只包含資料庫中存在的欄位
        const basePermission = {
          id: p.id,
          active: p.active ? 1 : 0,
          current_bindings: p.current_bindings,
          max_bindings: p.max_bindings,
          ip_check: p.ip_check ? 1 : 0,
          ip: p.ip_check ? p.ip : null,
          caster_on: p.caster_on ? 1 : 0,
          client_mode: p.client_mode
        };
        
        // 只有當 type=5 (MASTER) 時才包含 client_types 欄位
        if (p.type === '5') {
          basePermission.client_types = p.client_types || null;
        }
        
        // 處理PC多選的特殊情況
        if (p.type === 'PC' && p._actualType) {
          // 使用_actualType作為實際的type值
          return {
            ...basePermission,
            type: p._actualType  // 使用儲存的實際多選值
          };
        }
        
        // 一般情況，使用type欄位的值
        return {
          ...basePermission,
          type: p.type
        };
      });
      await updateUserPermissions(userId, { permissions: updatedPermissions });
      message.success('使用者權限更新成功');
    } catch (error) {
      console.error('Error updating user permissions:', error);
      message.error('Failed to update user permissions: ' + (error.response?.data?.message || error.message));
    }
  }, [userId, navigate]);

  const handleBack = useCallback(() => {
    navigate('/permissions');
  }, [navigate]);

  const handleEditBundle = useCallback((permissionId, bundle) => {
    setSelectedPermissionId(permissionId);
    setSelectedBundle(bundle);
    setBundleModalMode('edit');
    setIsBundleModalVisible(true);
  }, []);

  const handleAssignBundle = useCallback((permissionId) => {
    //如果permissionId為null，則跳出錯誤訊息
    if (!permissionId) {
      message.warning('在新的授權中分配Bundle時，請先儲存過一次');
      return;
    }
    setSelectedPermissionId(permissionId);
    setSelectedBundle(null);
    setBundleModalMode('assign');
    setIsBundleModalVisible(true);
  }, []);

  const handleDeleteBundle = useCallback(async (permissionId, bundleId) => {
    try {
      await deletePermissionsBundle(userId, permissionId, bundleId);
      message.success('Bundle deleted successfully');
      fetchUserPermissions();
    } catch (error) {
      console.error('Error deleting bundle:', error);
      message.error('Failed to delete bundle: ' + (error.response?.data?.message || error.message));
    }
  }, [userId, fetchUserPermissions]);

  const handleBundleModalSuccess = useCallback(() => {
    setIsBundleModalVisible(false);
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  const handleBundleModalCancel = useCallback(() => {
    setIsBundleModalVisible(false);
  }, []);

  const renderPermissionFields = useMemo(() => (fields, { add, remove }) => (
    <>
      {fields.map(({ key, name, ...restField }) => (
        <Card
          key={key}
          style={{ marginBottom: 16 }}
          type="inner"
          extra={
            <Space>
              <span style={{ fontSize: '12px', color: '#888' }}>
                Created at: {formatTime(form.getFieldValue(['permissions', name, 'created_at']))}
              </span>
              <Button type="text" danger onClick={() => remove(name)}>
                <DeleteOutlined />
              </Button>
            </Space>
          }
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <Form.Item
                {...restField}
                name={[name, 'active']}
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>
            <div style={{ flex: 1 }}>
              <Form.Item
                {...restField}
                name={[name, 'type']}
                label="Type"
                rules={[{ required: true, message: 'Type is required' }]}
              >
                <Select
                  onChange={(value) => {
                    console.log(`Type changed for permission ${name}:`, value);
                    
                    // 當選擇了PC時，初始化子選項為空字串
                    if (value === 'PC') {
                      setTimeout(() => {
                        form.setFieldValue(['permissions', name, 'pcSubTypes'], "");
                        form.setFieldValue(['permissions', name, '_actualType'], "");
                      }, 0);
                    } else {
                      // 如果選擇了其他選項，清空PC子選項
                      form.setFieldValue(['permissions', name, 'pcSubTypes'], undefined);
                    }
                  }}
                >
                  {(() => {
                    const allPermissions = form.getFieldValue('permissions') || [];
                    const currentSelectedType = form.getFieldValue(['permissions', name, 'type']);
                    
                    // 針對其他permission，獲取所有已選擇的type
                    // 排除PC選項，因為PC是特殊類型，會轉換成子選項
                    const chosenTypes = allPermissions
                      .filter((p) => p && p.type !== undefined && p.type !== currentSelectedType && p.type !== 'PC')
                      .map((p) => p.type);
                    
                    // 過濾選項
                    const availableOptions = deviceTypeOptions.filter(
                      (option) => {
                        // 如果是當前已選值，或未被其他選中，則可選
                        return option.value === currentSelectedType || !chosenTypes.includes(option.value);
                      }
                    );

                    return availableOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ));
                  })()}
                </Select>
              </Form.Item>
              
              {/* 隱藏欄位，用於存儲實際的type值 */}
              <Form.Item
                {...restField}
                name={[name, '_actualType']}
                hidden
              >
                <Input />
              </Form.Item>
              
              {/* PC子選項 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.permissions[name]?.type !== currentValues.permissions[name]?.type
                }
              >
                {({ getFieldValue }) => {
                  const typeValue = getFieldValue(['permissions', name, 'type']);
                  
                  // 如果選擇了PC，顯示PC子選項
                  return typeValue === 'PC' ? (
                    <Form.Item
                      {...restField}
                      name={[name, 'pcSubTypes']}
                      label="PC 類型選項"
                      initialValue=""
                      rules={[{ required: true, message: '請選擇一個PC類型' }]}
                    >
                      <Radio.Group
                        options={pcSubOptions}
                        onChange={(e) => {
                          // Radio.Group 返回的是單一值，而非數組
                          const value = e.target.value;
                          form.setFieldValue(['permissions', name, '_actualType'], value);
                        }}
                      />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
              
              {/* MASTER客戶端選項 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.permissions[name]?.type !== currentValues.permissions[name]?.type
                }
              >
                {({ getFieldValue }) => {
                  const typeValue = getFieldValue(['permissions', name, 'type']);
                  
                  // 如果選擇了MASTER (type=5)，顯示客戶端選項
                  return typeValue === '5' ? (
                    <Form.Item
                      {...restField}
                      name={[name, 'masterClientTypes']}
                      label="客戶端類型選項"
                      initialValue={getFieldValue(['permissions', name, 'client_types']) ? 
                        getFieldValue(['permissions', name, 'client_types']).split(',') : 
                        []}
                    >
                      <Checkbox.Group
                        options={pcSubOptions}
                        onChange={(checkedValues) => {
                          const typeStr = checkedValues && checkedValues.length > 0 ? 
                                        checkedValues.join(',') : '';
                          form.setFieldValue(['permissions', name, 'client_types'], typeStr);
                        }}
                      />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <Form.Item
                {...restField}
                name={[name, 'current_bindings']}
                label="Current Bindings (Only Read)"
              >
                <InputNumber min={0} readOnly />
              </Form.Item>
            </div>
            <div style={{ flex: 1 }}>
              <Form.Item
                {...restField}
                name={[name, 'max_bindings']}
                label="Max Bindings (0 = Unlimited)"
                rules={[{ required: true, message: 'Max bindings is required' }]}
              >
                <InputNumber min={0} />
              </Form.Item>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ marginLeft: 'auto', maxWidth: '50%', width: '100%' }}>
              <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.permissions[name]?.type !== currentValues.permissions[name]?.type
                  }
                >
                  {({ getFieldValue }) => {
                    const typeValue = getFieldValue(['permissions', name, 'type']);
                    console.log(`Current type for permission ${name}:`, typeValue);
                    
                    // 處理type可能是逗號分隔的情況
                    const typeValues = typeValue ? typeValue.split(',') : [];
                    // 如果任一值大於4則顯示Client Mode
                    return typeValues.some(v => parseInt(v) > 4) ? (
                      <Form.Item
                        {...restField}
                        name={[name, 'client_mode']}
                        label="Client Mode"
                      >
                        <Select
                          onChange={(value) => {
                            console.log(`Client Mode changed for permission ${name}:`, value);
                          }}
                        >
                          {clientModeOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ) : null;
                  }}
              </Form.Item>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ marginLeft: 'auto', maxWidth: '50%', width: '100%' }}>
              <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.permissions[name]?.type !== currentValues.permissions[name]?.type
                  }
                >
                  {({ getFieldValue }) => {
                    const typeValue = getFieldValue(['permissions', name, 'type']);
                    // 處理type可能是逗號分隔的情況
                    const typeValues = typeValue ? typeValue.split(',') : [];
                    // 檢查是否包含5 (Master)
                    return typeValues.includes('5') ? (
                      <Form.Item
                        {...restField}
                        name={[name, 'caster_on']}
                        label="CASTER ON/OFF"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    ) : null;
                  }}
              </Form.Item>
            </div>
          </div>


          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.permissions[name]?.type !== currentValues.permissions[name]?.type
                }
              >
                {({ getFieldValue }) => {
                  const typeValue = getFieldValue(['permissions', name, 'type']);
                  // 處理type可能是逗號分隔的情況
                  const typeValues = typeValue ? typeValue.split(',') : [];
                  // 檢查是否不包含4
                  return !typeValues.includes('4') ? (
                    <Form.Item
                      {...restField}
                      name={[name, 'ip_check']}
                      label="IP Check (在登入時進行IP確認)"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
            </div>
            <div style={{ flex: 1 }}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => {
                  const prevType = prevValues.permissions[name]?.type;
                  const currentType = currentValues.permissions[name]?.type;
                  const prevIpCheck = prevValues.permissions[name]?.ip_check;
                  const currentIpCheck = currentValues.permissions[name]?.ip_check;
                  return prevIpCheck !== currentIpCheck || prevType !== currentType;
                }}
              >
                {({ getFieldValue }) => {
                  const typeValue = getFieldValue(['permissions', name, 'type']);
                  const ipCheckValue = getFieldValue(['permissions', name, 'ip_check']);
                  
                  // 處理type可能是逗號分隔的情況
                  const typeValues = typeValue ? typeValue.split(',') : [];
                  
                  // 檢查是否不包含4且ip_check為true
                  return !typeValues.includes('4') && ipCheckValue ? (
                    <Form.Item
                      {...restField}
                      name={[name, 'ip']}
                      label="IP (若開啟IP Check 自動抓取)"
                      rules={[{ message: 'IP is required when IP Check is enabled' }]}
                    >
                      <Input />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
            </div>
          </div>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.permissions[name]?.type !== currentValues.permissions[name]?.type
            }
          >
            {({ getFieldValue }) => {
              const typeValue = getFieldValue(['permissions', name, 'type']);
              
              // 處理type可能是逗號分隔的情況
              let typeValues = [];
              
              // 如果是PC類型，要使用_actualType中存儲的實際值
              if (typeValue === 'PC') {
                const actualType = getFieldValue(['permissions', name, '_actualType']);
                typeValues = actualType ? actualType.split(',') : [];
              } else {
                typeValues = typeValue ? typeValue.split(',') : [];
              }
              
              // 判斷是否顯示Bundle清單：如果所有type值都小於5，則顯示Bundle清單
              return typeValues.length > 0 && typeValues.every(v => parseInt(v) < 5) ? (
                <BundleList
                  bundles={bundles[getFieldValue(['permissions', name, 'id'])] || []}
                  permissionId={getFieldValue(['permissions', name, 'id'])}
                  onEditBundle={handleEditBundle}
                  onAssignBundle={handleAssignBundle}
                  onDeleteBundle={handleDeleteBundle}
                />
              ) : null;
            }}
          </Form.Item>
        </Card>
      ))}
      <Form.Item>
        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
          Add Permission
        </Button>
      </Form.Item>
    </>
  ), [bundles, form, handleAssignBundle, handleEditBundle, handleDeleteBundle]);
  
  if (loading) {
    return <div className="loading-container"><Spin size="large" /></div>;
  }

  return (
    <div className="edit-user-container">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back to Permissions
        </Button>
        <Card
          title={<h2>正在編輯用戶授權：{user?.name}</h2>}
          className="edit-user-card"
        >
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.List name="permissions">
              {renderPermissionFields}
            </Form.List>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Update Permissions
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Space>
      <BundleModal
        visible={isBundleModalVisible}
        onCancel={handleBundleModalCancel}
        onSuccess={handleBundleModalSuccess}
        permissionId={selectedPermissionId}
        userId={userId}
        bundle={selectedBundle}
        mode={bundleModalMode}
        assignedBundles={bundles[selectedPermissionId] || []}
      />
    </div>
  );
};

export default React.memo(EditUserPermissions);
